import type { PromotionApplyMode, PromotionKind, PromotionUpsertInput } from '../domain/promotionTypes';

export type ValidationError = { title: string; message: string };

export function validatePromotionInput(
  input: PromotionUpsertInput,
  opts: {
    kind: PromotionKind;
    applyMode: PromotionApplyMode;
    scheduleStartTime: string;
    scheduleEndTime: string;
  }
): ValidationError | null {
  if (!input.name) {
    return { title: 'Nombre requerido', message: 'Poné un nombre a la promo.' };
  }

  if (opts.kind === 'percent_off' && (input.valuePercent == null || input.valuePercent <= 0 || input.valuePercent > 100)) {
    return { title: 'Valor inválido', message: 'Ingresá un porcentaje entre 1 y 100.' };
  }

  if (opts.kind === 'fixed_off' && (input.valueFixed == null || input.valueFixed <= 0)) {
    return { title: 'Valor inválido', message: 'Ingresá un monto fijo mayor a 0.' };
  }

  if (opts.kind === 'combo_buy_x_get_y') {
    const buy = input.comboBuyQty ?? 0;
    const pay = input.comboPayQty ?? 0;
    if (buy < 2 || pay < 1 || pay >= buy) {
      return { title: 'Combo inválido', message: 'En 2×1 (o 3×2): comprás 2 o más y pagás menos que eso.' };
    }
  }

  if (opts.kind === 'combo_n_for_price') {
    const buy = input.comboBuyQty ?? 0;
    const bundle = input.comboBundlePrice ?? 0;
    if (buy < 2 || bundle <= 0) {
      return { title: 'Combo inválido', message: 'En combo por precio: comprás 2 o más y el precio tiene que ser mayor a 0.' };
    }
  }

  if (opts.applyMode === 'manual_code' && !input.promoCode?.trim()) {
    return { title: 'Código requerido', message: 'Poné el código. Este modo lo necesita.' };
  }

  if (input.startsAt && input.endsAt && input.endsAt < input.startsAt) {
    return { title: 'Fechas inválidas', message: 'La fecha de fin no puede ser antes que la de inicio.' };
  }

  const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  const hasStart = opts.scheduleStartTime.trim().length > 0;
  const hasEnd = opts.scheduleEndTime.trim().length > 0;
  if (hasStart && !TIME_REGEX.test(opts.scheduleStartTime.trim())) {
    return { title: 'Horario inválido', message: 'Usá formato HH:mm, ej. 14:30.' };
  }
  if (hasEnd && !TIME_REGEX.test(opts.scheduleEndTime.trim())) {
    return { title: 'Horario inválido', message: 'Usá formato HH:mm, ej. 18:00.' };
  }
  // Partial schedule window: one field set, the other empty → error (don't silently drop).
  if (hasStart !== hasEnd) {
    return { title: 'Horario incompleto', message: 'Completá horario de inicio y fin, o dejá ambos vacíos.' };
  }
  // Cross-midnight windows (e.g. 22:00–02:00) are valid: end < start means the
  // window crosses midnight. No rejection needed — the engine handles both cases.

  if (opts.applyMode === 'cashier_select' && input.channels?.includes('online')) {
    return { title: 'Canal inválido', message: 'La selección de cajero solo funciona en POS.' };
  }

  return null;
}
